import React from 'react';
import { TCN, TCN_COLOR_MAP } from './tokens';

/**
 * TCN Chip — small status pill.
 *
 * Props:
 *  - color: semantic key ('cyan'|'magenta'|'green'|'amber'|'red'|'diamond') or any CSS color
 *  - bg: optional explicit bg override (else derived from color@1f hex)
 *  - solid: boolean — if true, bg = color, text = #000 (high-emphasis)
 *  - size: 'sm' | 'md' | 'lg' (default 'md')
 *  - leading, trailing — optional small icon slots
 */
// Aligned on mockup ui-kit.jsx:259-277 — paddings serrés, fontWeight 700
const SIZE = {
  sm: { padX: 6, padY: 2, fs: 10, gap: 4 },
  md: { padX: 8, padY: 3, fs: 11, gap: 5 },
  lg: { padX: 10, padY: 4, fs: 12, gap: 6 },
};

const withAlpha = (hex, alphaHex) => {
  if (!hex) return null;
  // If already an rgba/hsl, just append cannot work — return as-is.
  if (!hex.startsWith('#')) return hex;
  // Expand short hex
  let h = hex.length === 4
    ? '#' + [...hex.slice(1)].map((c) => c + c).join('')
    : hex;
  return `${h}${alphaHex}`;
};

const Chip = ({
  color = 'cyan',
  bg,
  solid = false,
  size = 'md',
  leading,
  trailing,
  className = '',
  style,
  children,
  onClick,
  ...rest
}) => {
  const c = TCN_COLOR_MAP[color] ?? color;
  const sz = SIZE[size] ?? SIZE.md;

  const styleSolid = {
    background: c,
    color: '#000',
    border: '1px solid transparent',
  };
  const styleOutline = {
    background: bg ?? withAlpha(c, '1f') ?? `${c}1f`,
    color: c,
    border: `1px solid ${withAlpha(c, '33') ?? `${c}33`}`,
  };

  return (
    <span
      onClick={onClick}
      className={`inline-flex items-center whitespace-nowrap ${onClick ? 'cursor-pointer' : ''} ${className}`}
      style={{
        padding: `${sz.padY}px ${sz.padX}px`,
        gap: sz.gap,
        fontSize: sz.fs,
        fontWeight: 700,                  // mockup ui-kit.jsx — was Tailwind font-semibold (600)
        borderRadius: TCN.radius.pill,
        letterSpacing: '0.018em',         // mockup raw 0.2 ≈ 0.018em
        fontFamily: TCN.fontSans,
        lineHeight: 1.2,
        ...(solid ? styleSolid : styleOutline),
        ...style,
      }}
      {...rest}
    >
      {leading}
      <span>{children}</span>
      {trailing}
    </span>
  );
};

export default Chip;
